import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, ExternalLink, ChevronUp, ChevronDown, Grid3x3 } from "lucide-react";
import { api, mediaUrl, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectForm from "@/pages/admin/ProjectForm";
import FeedForm from "@/pages/admin/FeedForm";
import ContentForm from "@/pages/admin/ContentForm";

const BUDGETS = ["Under ₹50k", "₹50k – 1.5L", "₹1.5L – 5L", "₹5L+"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | project
  const [editingFeed, setEditingFeed] = useState(null); // null | {} | feed
  const [leadFilter, setLeadFilter] = useState({ budget: "All", service: "All" });

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  const loadFeeds = () => api.get("/feeds").then((r) => setFeeds(r.data)).catch(() => {});
  const loadLeads = () => {
    const params = {};
    if (leadFilter.budget !== "All") params.budget = leadFilter.budget;
    if (leadFilter.service !== "All") params.service = leadFilter.service;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).catch(() => {});
  };

  useEffect(() => { loadProjects(); loadFeeds(); }, []);
  useEffect(() => { if (tab === "leads") loadLeads(); /* eslint-disable-next-line */ }, [tab, leadFilter]);

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  const delProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/admin/projects/${id}`); toast.success("Deleted"); loadProjects(); }
    catch { toast.error("Delete failed"); }
  };
  const delFeed = async (id) => {
    if (!window.confirm("Delete this feed?")) return;
    try { await api.delete(`/admin/feeds/${id}`); toast.success("Deleted"); loadFeeds(); }
    catch { toast.error("Delete failed"); }
  };
  const delLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await api.delete(`/admin/leads/${id}`); toast.success("Deleted"); loadLeads(); }
    catch { toast.error("Delete failed"); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return "—"; } };

  const moveProject = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= projects.length) return;
    const arr = [...projects];
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setProjects(arr); // optimistic
    try { await api.post("/admin/projects/reorder", { ids: arr.map((p) => p.id) }); }
    catch { toast.error("Reorder failed"); loadProjects(); }
  };

  const moveFeed = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= feeds.length) return;
    const arr = [...feeds];
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setFeeds(arr); // optimistic
    try { await api.post("/admin/feeds/reorder", { ids: arr.map((f) => f.id) }); }
    catch { toast.error("Reorder failed"); loadFeeds(); }
  };

  return (
    <div className="min-h-screen bg-bg">
      <header className="border-b border-border sticky top-0 bg-bg/90 backdrop-blur z-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <span className="font-serif text-xl">Qissa<span className="text-accent">.</span>Co <span className="text-muted2 text-sm">/ admin</span></span>
            <nav className="flex gap-1">
              {["projects", "feeds", "leads", "content"].map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                  className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${tab === t ? "bg-accent text-bg" : "text-muted2 hover:text-ink"}`}>
                  {t}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-muted2 text-sm hidden md:inline">{user?.email}</span>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-muted2 hover:text-ink text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        {tab === "projects" && (
          <div data-testid="projects-panel">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-serif text-3xl">Projects <span className="text-muted2 text-lg">({projects.length})</span></h1>
              <button onClick={() => setEditing({})} data-testid="new-project-btn" className="inline-flex items-center gap-2 rounded-full bg-accent text-bg px-5 py-2.5 text-sm font-medium hover:bg-accent-hover"><Plus size={16} /> New project</button>
            </div>
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm" data-testid="projects-table">
                <thead className="bg-surface text-muted2">
                  <tr className="text-left">
                    <th className="p-4 font-medium">Order</th>
                    <th className="p-4 font-medium">Project</th>
                    <th className="p-4 font-medium hidden md:table-cell">Category</th>
                    <th className="p-4 font-medium hidden md:table-cell">Year</th>
                    <th className="p-4 font-medium">Featured</th>
                    <th className="p-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, idx) => (
                    <tr key={p.id} className="border-t border-border hover:bg-surface/50">
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveProject(idx, -1)} disabled={idx === 0}
                            data-testid={`move-up-${p.slug}`}
                            className="text-muted2 hover:text-lime disabled:opacity-20 disabled:hover:text-muted2"><ChevronUp size={16} /></button>
                          <button onClick={() => moveProject(idx, 1)} disabled={idx === projects.length - 1}
                            data-testid={`move-down-${p.slug}`}
                            className="text-muted2 hover:text-lime disabled:opacity-20 disabled:hover:text-muted2"><ChevronDown size={16} /></button>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {p.cover && <img src={mediaUrl(p.cover)} alt="" className="w-12 h-9 object-cover rounded" />}
                          <span className="font-medium text-ink">{p.title}</span>
                        </div>
                      </td>
                      <td className="p-4 text-muted2 hidden md:table-cell">{(p.categories && p.categories.length ? p.categories : [p.category]).join(", ")}</td>
                      <td className="p-4 text-muted2 hidden md:table-cell">{p.year}</td>
                      <td className="p-4">{p.featured ? <span className="text-accent">●</span> : <span className="text-muted2">○</span>}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-3 text-muted2">
                          <a href={`/work/${p.slug}`} target="_blank" rel="noreferrer" className="hover:text-ink"><ExternalLink size={16} /></a>
                          <button onClick={() => setEditing(p)} data-testid={`edit-${p.slug}`} className="hover:text-ink"><Pencil size={16} /></button>
                          <button onClick={() => delProject(p.id)} data-testid={`delete-${p.slug}`} className="hover:text-accent"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "feeds" && (
          <div data-testid="feeds-panel">
            <div className="flex items-center justify-between mb-2">
              <h1 className="font-serif text-3xl">Social Feeds <span className="text-muted2 text-lg">({feeds.length})</span></h1>
              <button onClick={() => setEditingFeed({})} data-testid="new-feed-btn" className="inline-flex items-center gap-2 rounded-full bg-accent text-bg px-5 py-2.5 text-sm font-medium hover:bg-accent-hover"><Plus size={16} /> New feed</button>
            </div>
            <p className="text-muted2 text-sm mb-6">Short Instagram gigs shown under the <span className="text-ink">Social Media</span> filter on the Work page, grids, posts & reels grouped by brand.</p>
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-sm" data-testid="feeds-table">
                <thead className="bg-surface text-muted2">
                  <tr className="text-left">
                    <th className="p-4 font-medium">Order</th>
                    <th className="p-4 font-medium">Brand</th>
                    <th className="p-4 font-medium hidden md:table-cell">Layout</th>
                    <th className="p-4 font-medium hidden md:table-cell">Posts</th>
                    <th className="p-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feeds.map((f, idx) => (
                    <tr key={f.id} className="border-t border-border hover:bg-surface/50">
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => moveFeed(idx, -1)} disabled={idx === 0} data-testid={`feed-up-${idx}`} className="text-muted2 hover:text-lime disabled:opacity-20 disabled:hover:text-muted2"><ChevronUp size={16} /></button>
                          <button onClick={() => moveFeed(idx, 1)} disabled={idx === feeds.length - 1} data-testid={`feed-down-${idx}`} className="text-muted2 hover:text-lime disabled:opacity-20 disabled:hover:text-muted2"><ChevronDown size={16} /></button>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {f.posts && f.posts[0] ? (
                            f.posts[0].type === "video"
                              ? <video src={mediaUrl(f.posts[0].url)} className="w-10 h-10 object-cover rounded" />
                              : <img src={mediaUrl(f.posts[0].url)} alt="" className="w-10 h-10 object-cover rounded" />
                          ) : <span className="w-10 h-10 rounded bg-surface flex items-center justify-center text-muted2"><Grid3x3 size={16} /></span>}
                          <div>
                            <span className="font-medium text-ink block">{f.brand}</span>
                            <span className="text-muted2 text-xs">{(f.categories && f.categories.length ? f.categories : ["Social Media"]).join(" · ")}</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-muted2 hidden md:table-cell capitalize">{f.layout}</td>
                      <td className="p-4 text-muted2 hidden md:table-cell">{(f.posts || []).length}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-3 text-muted2">
                          <button onClick={() => setEditingFeed(f)} data-testid={`edit-feed-${idx}`} className="hover:text-ink"><Pencil size={16} /></button>
                          <button onClick={() => delFeed(f.id)} data-testid={`delete-feed-${idx}`} className="hover:text-accent"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {feeds.length === 0 && <p className="text-muted2 p-8 text-center">No feeds yet, add your first short social gig.</p>}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div data-testid="leads-panel">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <h1 className="font-serif text-3xl">Leads <span className="text-muted2 text-lg">({leads.length})</span></h1>
              <div className="flex gap-3">
                <select data-testid="lead-filter-budget" value={leadFilter.budget} onChange={(e) => setLeadFilter((f) => ({ ...f, budget: e.target.value }))}
                  className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-ink">
                  <option value="All">All budgets</option>
                  {BUDGETS.map((b) => <option key={b} value={b} className="bg-surface">{b}</option>)}
                </select>
                <select data-testid="lead-filter-service" value={leadFilter.service} onChange={(e) => setLeadFilter((f) => ({ ...f, service: e.target.value }))}
                  className="bg-surface border border-border rounded-lg px-3 py-2 text-sm text-ink">
                  <option value="All">All services</option>
                  {["Branding", "Social Media", "AI Creatives", "AI Videos", "UI UX", "Catalog/Publication", "Other"].map((s) => <option key={s} value={s} className="bg-surface">{s}</option>)}
                </select>
              </div>
            </div>
            <div className="rounded-xl border border-border overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]" data-testid="leads-table">
                <thead className="bg-surface text-muted2">
                  <tr className="text-left">
                    <th className="p-4 font-medium">Date</th>
                    <th className="p-4 font-medium">Name</th>
                    <th className="p-4 font-medium">Brand</th>
                    <th className="p-4 font-medium">Needs</th>
                    <th className="p-4 font-medium">Budget</th>
                    <th className="p-4 font-medium">Timeline</th>
                    <th className="p-4 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-t border-border align-top hover:bg-surface/50">
                      <td className="p-4 text-muted2 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                      <td className="p-4">
                        <div className="text-ink font-medium">{l.name}</div>
                        <div className="text-muted2 text-xs">{l.handle}</div>
                        {l.message && <div className="text-muted2 text-xs mt-1 max-w-xs">{l.message}</div>}
                      </td>
                      <td className="p-4 text-muted2">{l.brand || "—"}</td>
                      <td className="p-4"><div className="flex flex-wrap gap-1">{(l.needs || []).map((n) => <span key={n} className="rounded-full border border-border px-2 py-0.5 text-xs text-ink">{n}</span>)}</div></td>
                      <td className="p-4 text-ink whitespace-nowrap">{l.budget || "—"}</td>
                      <td className="p-4 text-muted2 whitespace-nowrap">{l.timeline || "—"}</td>
                      <td className="p-4 text-right"><button onClick={() => delLead(l.id)} className="text-muted2 hover:text-accent"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {leads.length === 0 && <p className="text-muted2 p-8 text-center">No leads yet.</p>}
            </div>
          </div>
        )}
        {tab === "content" && <ContentForm />}
      </main>

      {editing !== null && (
        <ProjectForm
          initial={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadProjects(); }}
        />
      )}

      {editingFeed !== null && (
        <FeedForm
          initial={editingFeed.id ? editingFeed : null}
          onClose={() => setEditingFeed(null)}
          onSaved={() => { setEditingFeed(null); loadFeeds(); }}
        />
      )}
    </div>
  );
}
